interface MsgDeskLoadingProps {
  contract?: string
  variant?: 'page' | 'panel' | 'inline' | 'button' | 'overlay'
  label?: string
  className?: string
}

function Spinner({ className = '' }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={`inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-r-transparent ${className}`}
    />
  )
}

export function MsgDeskLoading({
  contract,
  variant = 'page',
  label,
  className = '',
}: MsgDeskLoadingProps) {
  const text = label || (variant === 'button' ? 'Saving' : 'Loading')

  if (variant === 'button') {
    return (
      <span
        data-md-contract={contract}
        role="status"
        aria-live="polite"
        className={`inline-flex items-center gap-2 ${className}`}
      >
        <Spinner />
        <span>{text}</span>
      </span>
    )
  }

  if (variant === 'inline') {
    return (
      <span
        data-md-contract={contract}
        role="status"
        aria-live="polite"
        className={`inline-flex items-center gap-1.5 rounded-full bg-[#0f766e14] px-2 py-0.5 text-[11px] font-semibold text-[#0f766e] ${className}`}
      >
        <Spinner className="h-3 w-3" />
        <span>{text}</span>
      </span>
    )
  }

  if (variant === 'overlay') {
    return (
      <div
        data-md-contract={contract}
        role="status"
        aria-live="polite"
        aria-label={text}
        className={`inline-flex min-w-[220px] items-center gap-3 rounded-lg border border-[#d9e2ea] bg-white px-4 py-3 text-sm font-semibold text-[#172033] shadow-[0_16px_40px_rgba(15,23,42,0.18)] ${className}`}
      >
        <Spinner className="h-5 w-5 text-[#0f766e]" />
        <span>{text}</span>
      </div>
    )
  }

  if (variant === 'panel') {
    return (
      <div
        data-md-contract={contract}
        role="status"
        aria-live="polite"
        aria-label={text}
        className={`rounded-lg border border-[#d9e2ea] bg-white p-4 text-[#172033] ${className}`}
      >
        <div className="mb-4 flex items-center gap-3">
          <Spinner className="h-5 w-5 text-[#0f766e]" />
          <span className="text-sm font-semibold">{text}</span>
        </div>
        <div className="space-y-2">
          <div className="h-2.5 w-full animate-pulse rounded bg-[#e3eaf0]" />
          <div className="h-2.5 w-5/6 animate-pulse rounded bg-[#e3eaf0]" />
          <div className="h-2.5 w-2/3 animate-pulse rounded bg-[#e3eaf0]" />
        </div>
      </div>
    )
  }

  return (
    <div
      data-md-contract={contract}
      role="status"
      aria-live="polite"
      aria-label={text}
      className={`min-h-screen bg-[#f7fafc] text-[#172033] ${className}`}
    >
      <div className="mx-auto flex min-h-screen w-full max-w-6xl flex-col px-4 py-5 sm:px-6 lg:px-8">
        <div className="mb-5 flex items-center justify-between border-b border-[#d9e2ea] pb-4">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-md bg-[#0f766e]" />
            <div>
              <div className="h-3 w-28 animate-pulse rounded bg-[#cfd9e2]" />
              <div className="mt-2 h-2 w-44 animate-pulse rounded bg-[#e3eaf0]" />
            </div>
          </div>
          <div className="hidden h-8 w-24 animate-pulse rounded-md bg-[#e3eaf0] sm:block" />
        </div>

        <div className="grid flex-1 gap-5 lg:grid-cols-[260px_1fr]">
          <aside className="hidden border-r border-[#d9e2ea] pr-5 lg:block">
            <div className="space-y-3">
              <div className="h-9 animate-pulse rounded-md bg-white shadow-[inset_0_0_0_1px_#d9e2ea]" />
              <div className="h-9 animate-pulse rounded-md bg-white shadow-[inset_0_0_0_1px_#d9e2ea]" />
              <div className="h-9 animate-pulse rounded-md bg-white shadow-[inset_0_0_0_1px_#d9e2ea]" />
            </div>
          </aside>

          <section className="min-w-0">
            <div className="mb-5 h-1 overflow-hidden rounded bg-[#d9e2ea]">
              <div className="h-full w-2/5 animate-pulse rounded bg-[#0f766e]" />
            </div>

            <div className="space-y-3">
              {[0, 1, 2, 3].map(index => (
                <div
                  key={index}
                  className="rounded-md border border-[#d9e2ea] bg-white px-4 py-3"
                >
                  <div className="flex items-start gap-3">
                    <div className="h-9 w-9 flex-none animate-pulse rounded-full bg-[#d9e2ea]" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-4">
                        <div className="h-3 w-36 animate-pulse rounded bg-[#cfd9e2]" />
                        <div className="h-2 w-20 animate-pulse rounded bg-[#e3eaf0]" />
                      </div>
                      <div className="mt-3 h-2 w-full animate-pulse rounded bg-[#e3eaf0]" />
                      <div className="mt-2 h-2 w-2/3 animate-pulse rounded bg-[#e3eaf0]" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}
